/// <reference lib="dom" />
/**
 * Auth singletons for `@ams/shared/auth`.
 *
 * Materials-03b Phase 1: provides the ONE physical `CsrfManager` and
 * `IdTokenManager` instance that the host (ops-platform-v3), BOM module,
 * inventory module, and any future module-package share.
 *
 * Phase-0 Vite/Rollup spike confirmed `@ams/shared` is deduped across the
 * host main chunk + module-package lazy chunks, so module-eval of this
 * file produces a single instance no matter how many lazy chunks reference it.
 *
 * Bootstrap contract:
 *   - The host (the only entry-point that knows the API + Functions URLs)
 *     calls `initAuthSingletons({ apiBase, functionsBase })` ONCE during
 *     app bootstrap. Module packages must NEVER call init — they only read.
 *   - `csrfManager` is constructed eagerly and is safe to import / use at
 *     any time (it has no init-time dependency; it's populated later from
 *     `/auth/login` + `/auth/verify` response headers via `setFromResponse`).
 *   - `idTokenManager` is exported as a stable proxy. Calling its methods
 *     before `initAuthSingletons` throws a clear error.
 *   - `initAuthSingletons` is idempotent for the same config; calling it
 *     twice with different config throws (guards against double-bootstrap
 *     leaking divergent endpoints).
 *   - `getAuthConfig()` reads the locked config; throws if not initialized.
 */
import { createCsrfManager } from './csrf-manager.js';
import { createIdTokenManager } from './id-token-manager.js';
// Eagerly created — no init dependency.
export const csrfManager = createCsrfManager();
let config = null;
let idTokenManagerImpl = null;
const NOT_INITIALIZED_MESSAGE = 'initAuthSingletons() must be called before idTokenManager.getIdToken()';
/**
 * Stable reference returned to callers. Methods delegate to the lazily
 * created concrete impl after `initAuthSingletons` has been called; throw
 * otherwise.
 */
export const idTokenManager = {
    async getIdToken() {
        if (idTokenManagerImpl == null) {
            throw new Error(NOT_INITIALIZED_MESSAGE);
        }
        return idTokenManagerImpl.getIdToken();
    },
    invalidate() {
        if (idTokenManagerImpl == null) {
            // Pre-init invalidate is a no-op — there's nothing to invalidate.
            return;
        }
        idTokenManagerImpl.invalidate();
    },
};
function configsEqual(a, b) {
    return (a.apiBase === b.apiBase &&
        a.functionsBase === b.functionsBase &&
        a.fetch === b.fetch &&
        a.now === b.now);
}
function describeConfig(c) {
    // fetch/now are functions and don't serialize meaningfully — only the
    // URL-shaped fields are useful in an error message.
    return JSON.stringify({ apiBase: c.apiBase, functionsBase: c.functionsBase });
}
/**
 * Bootstrap the auth singletons. Host (ops-platform-v3) calls this exactly
 * once during app start-up. Idempotent for matching config; throws on
 * divergent config to surface accidental double-bootstrap.
 */
export function initAuthSingletons(nextConfig) {
    if (config != null) {
        if (!configsEqual(config, nextConfig)) {
            throw new Error(`initAuthSingletons called twice with different config: ` +
                `existing=${describeConfig(config)}, next=${describeConfig(nextConfig)}`);
        }
        return;
    }
    config = {
        apiBase: nextConfig.apiBase,
        functionsBase: nextConfig.functionsBase,
        ...(nextConfig.fetch !== undefined ? { fetch: nextConfig.fetch } : {}),
        ...(nextConfig.now !== undefined ? { now: nextConfig.now } : {}),
    };
    idTokenManagerImpl = createIdTokenManager({
        apiBase: config.apiBase,
        csrfManager,
        ...(config.fetch !== undefined ? { fetch: config.fetch } : {}),
        ...(config.now !== undefined ? { now: config.now } : {}),
    });
}
/**
 * Read the locked auth config. Throws if `initAuthSingletons` has not been
 * called yet. Used by downstream consumers (BOM/inventory ApiClients) to
 * read `functionsBase` from a single authoritative place.
 */
export function getAuthConfig() {
    if (config == null) {
        throw new Error('initAuthSingletons() must be called before getAuthConfig()');
    }
    return config;
}
/**
 * Test-only escape hatch. Resets the module-level state so vitest cases can
 * assert the pre-init failure modes independently. Do NOT call from app code.
 */
export function __resetForTests() {
    config = null;
    idTokenManagerImpl = null;
    csrfManager.invalidate();
}
//# sourceMappingURL=singletons.js.map